import { useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useStore } from '@/hooks/useStore';
import {
  getLoansByFarmer, getSeasonsByFarmer, getDebtsByFarmer, getLimitsByFarmer,
  getFarmerKyc, getFarmerById,
  getActivePaymentIntentByLoan,
  getFarmerMoneySummary,
} from '@/store';
import { EmptyState } from '@/components/base/EmptyState';
import { showToast } from '@/components/base/Toast';
import { formatCompactCurrency } from '@/utils/format';
import { LoanFilters } from './components/LoanFilters';
import { LoanCard } from './components/LoanCard';
import { LoanDetailModal } from './components/LoanDetailModal';
import { ApplyLoanSheet } from '@/pages/farmer/info/components/ApplyLoanSheet';
import { RepayDebtSheet } from '@/pages/farmer/info/components/RepayDebtSheet';
import type { AdvanceRequest, Debt } from '@/types';

export default function FarmerLoanPage() {
  useStore();
  const { user } = useAuth();
  const farmerId = user?.farmerId || 'f1';

  const farmer = getFarmerById(farmerId);
  const kyc = farmer ? getFarmerKyc(farmer) : null;
  const isKycVerified = kyc?.kycStatus === 'ekyc_verified';

  const allLoans = getLoansByFarmer(farmerId);
  const seasons = getSeasonsByFarmer(farmerId);
  const debts = getDebtsByFarmer(farmerId);
  // getLimitsByFarmer kept for parity with other pages reading the farmer's limit
  // record; not directly rendered here beyond the money-summary figures below.
  void getLimitsByFarmer(farmerId);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterLoai, setFilterLoai] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [detailItem, setDetailItem] = useState<AdvanceRequest | null>(null);
  const [repayOpen, setRepayOpen] = useState(false);

  // Not memoized — see overview/page.tsx's note on why memoizing this on
  // farmerId alone freezes a stale (often all-zero) first-render snapshot.
  const moneySummary = getFarmerMoneySummary(farmerId);
  const duNo = moneySummary.duNo;

  const sapDenHan = debts.filter((d) => d.status === 'Sắp đến hạn' || d.status === 'Đến hạn');
  const quaHan = debts.filter((d) => d.status === 'Quá hạn');
  const hasOverdue = quaHan.length > 0;

  const moneyLoansOnly = allLoans.filter((l) => l.loai === 'Tiền');
  const materialLoansOnly = allLoans.filter((l) => l.loai === 'Vật tư');
  const matChoKiemTra = materialLoansOnly.filter((l) => l.status === 'Chờ kiểm tra').length;
  const matDaDuyet = materialLoansOnly.filter((l) => l.status === 'Đã duyệt' || l.status === 'Chờ nhập kho').length;
  const matDaCap = materialLoansOnly.filter((l) => l.status === 'Đã cấp vật tư').length;
  const matChoNhapKho = materialLoansOnly.filter((l) => l.status === 'Chờ nhập kho').length;

  const isMaterialFilter = filterLoai === 'Vật tư';

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { '': allLoans.length };
    allLoans.forEach((loan) => {
      counts[loan.status] = (counts[loan.status] || 0) + 1;
    });
    return counts;
  }, [allLoans]);

  const typeCounts = useMemo(() => ({
    '': allLoans.length,
    'Tiền': moneyLoansOnly.length,
    'Vật tư': materialLoansOnly.length,
  }), [allLoans.length, materialLoansOnly.length, moneyLoansOnly.length]);

  const filteredLoans = useMemo(() => {
    let list = [...allLoans];
    if (filterStatus) list = list.filter((l) => l.status === filterStatus);
    if (filterLoai) list = list.filter((l) => l.loai === filterLoai);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((l) =>
        l.id.toLowerCase().includes(q) ||
        l.mucDich.toLowerCase().includes(q) ||
        (l.tenVatTu || '').toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => (b.ngayYeuCau > a.ngayYeuCau ? 1 : -1));
  }, [allLoans, filterStatus, filterLoai, search]);

  const handleDelete = (item: AdvanceRequest) => {
    if (item.status !== 'Chờ kiểm tra') {
      showToast('Chỉ có thể xóa yêu cầu đang chờ HTX kiểm tra', 'warning');
      return;
    }
    showToast('Chưa hỗ trợ xóa yêu cầu vay. Vui lòng liên hệ HTX nếu cần hủy yêu cầu này.', 'warning');
  };

  const activeDebts = debts.filter((d) => d.status !== 'Đã tất toán');

  // Helper: get display status considering the linked PaymentIntent
  const getDebtDisplayStatus = (debt: Debt): { label: string; className: string } => {
    const pi = getActivePaymentIntentByLoan(farmerId, debt.advanceRequestId || debt.id);
    if (pi) {
      if (pi.status === 'farmer_confirmed_transfer' || pi.status === 'waiting_htx_confirmation') {
        return { label: 'Chờ HTX xác nhận', className: 'bg-sky-50 text-sky-700' };
      }
      if (pi.status === 'reconcile_failed') {
        return { label: 'Đối soát lỗi', className: 'bg-red-50 text-red-700' };
      }
      if (pi.status === 'qr_created') {
        return { label: 'Chờ xác nhận CK', className: 'bg-orange-50 text-orange-700' };
      }
    }
    const map: Record<string, { label: string; className: string }> = {
      'Đang vay': { label: 'Đang vay', className: 'bg-slate-100 text-slate-600' },
      'Sắp đến hạn': { label: 'Sắp đến hạn', className: 'bg-amber-50 text-amber-700' },
      'Đến hạn': { label: 'Đến hạn', className: 'bg-orange-50 text-orange-700' },
      'Quá hạn': { label: 'Quá hạn', className: 'bg-red-50 text-red-700' },
      'Đã tất toán': { label: 'Đã tất toán', className: 'bg-agrida-50 text-agrida-700' },
    };
    return map[debt.status] || { label: debt.status, className: 'bg-slate-100 text-slate-600' };
  };

  return (
    <div className="pb-4 max-w-[800px] mx-auto">
      {/* Credit Limit Section */}
      <div className="mx-4 mt-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">Vay vốn</h2>
          {!isKycVerified && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 border border-amber-200 text-[10px] font-medium text-amber-700">
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-shield-user-line text-[10px]" /></span>
              Chưa eKYC
            </span>
          )}
        </div>

        {/* Dư nợ tiền — chỉ hiện khi KHÔNG lọc Vật tư */}
        {!isMaterialFilter && (
          <div className="flex gap-2">
            <div className={`flex-1 rounded-xl border p-3 ${duNo > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Dư nợ tiền</p>
              <p className={`text-sm font-bold ${duNo > 0 ? 'text-amber-700' : 'text-slate-500'}`}>
                {formatCompactCurrency(duNo)}
              </p>
            </div>
            {(sapDenHan.length > 0 || quaHan.length > 0) && (
              <div className={`flex-1 rounded-xl border p-3 ${quaHan.length > 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">
                  {quaHan.length > 0 ? 'Quá hạn' : 'Sắp đến hạn'}
                </p>
                <p className={`text-sm font-bold ${quaHan.length > 0 ? 'text-red-700' : 'text-amber-700'}`}>
                  {quaHan.length > 0 ? quaHan.length : sapDenHan.length} khoản
                </p>
              </div>
            )}
          </div>
        )}

        {/* Vật tư summary — chỉ hiện khi lọc Vật tư */}
        {isMaterialFilter && (
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Yêu cầu VT</p>
              <p className="text-sm font-bold text-slate-800">{materialLoansOnly.length}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Chờ xử lý</p>
              <p className="text-sm font-bold text-amber-600">{matChoKiemTra + matDaDuyet + matChoNhapKho}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Đã cấp</p>
              <p className="text-sm font-bold text-agrida-600">{matDaCap}</p>
            </div>
          </div>
        )}

        {/* eKYC warning / overdue warning — chỉ khi không lọc Vật tư */}
        {!isMaterialFilter && (
          <>
            {!isKycVerified && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                <span className="w-5 h-5 flex items-center justify-center shrink-0 text-amber-600 mt-0.5">
                  <i className="ri-error-warning-line" />
                </span>
                <div>
                  <p className="text-xs font-medium text-amber-800">Chưa xác thực eKYC</p>
                  <p className="text-[10px] text-amber-600 mt-0.5">Bạn cần hoàn tất eKYC trước khi gửi yêu cầu vay vốn.</p>
                </div>
              </div>
            )}

            {hasOverdue && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                <span className="w-5 h-5 flex items-center justify-center shrink-0 text-red-600 mt-0.5">
                  <i className="ri-alert-line" />
                </span>
                <div>
                  <p className="text-xs font-medium text-red-800">Có {quaHan.length} khoản nợ quá hạn</p>
                  <p className="text-[10px] text-red-600 mt-0.5">
                    Vui lòng thanh toán khoản quá hạn trước khi tạo yêu cầu vay mới.
                    Tổng nợ quá hạn: {formatCompactCurrency(quaHan.reduce((s, d) => s + d.conNo, 0))}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Active debts with QR payment */}
      {activeDebts.length > 0 && (
        <div className="mx-4 mt-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-900">Khoản vay đang trả</h3>
            <span className="text-[10px] text-slate-400">{activeDebts.length} khoản</span>
          </div>
          <div className="space-y-2">
            {activeDebts.map((debt) => {
              const ds = getDebtDisplayStatus(debt);
              const isPendingPI = ds.label === 'Chờ HTX xác nhận' || ds.label === 'Chờ xác nhận CK' || ds.label === 'Đối soát lỗi';
              return (
              <div key={debt.id} className="bg-white rounded-xl border border-slate-200 p-3 flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-800">{debt.maKhoanVay || debt.id}</span>
                    <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[9px] font-medium ${ds.className}`}>{ds.label}</span>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    Còn nợ: <span className="font-semibold text-slate-700">{formatCompactCurrency(debt.conNo)}</span>
                    {' '}· Hạn: {debt.ngayDenHan}
                  </p>
                </div>
                {!isPendingPI && debt.conNo > 0 && (
                  <button
                    type="button"
                    onClick={() => setRepayOpen(true)}
                    className="shrink-0 ml-3 px-3 py-2 rounded-xl text-xs font-medium bg-agrida-600 hover:bg-agrida-700 text-white transition-colors whitespace-nowrap min-h-[44px] flex items-center gap-1"
                  >
                    <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-qr-code-line" /></span>
                    Trả ngay
                  </button>
                )}
                {isPendingPI && (
                  <span className="shrink-0 ml-3 px-3 py-2 rounded-xl text-xs font-medium bg-amber-100 text-amber-700 whitespace-nowrap flex items-center gap-1">
                    <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-time-line" /></span>
                    Đang xử lý
                  </span>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Loan Request List */}
      <div className="mx-4 mt-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-900">Yêu cầu vay</h3>
          <div className="flex gap-2">
            <button type="button" onClick={() => setFormOpen(true)} className="flex min-h-11 items-center gap-1 rounded-xl bg-agrida-600 px-3 text-xs font-bold text-white shadow-lg shadow-agrida-600/15 transition hover:bg-agrida-700 active:scale-[0.98]"><i className="ri-add-line" />Xin vay</button>
          </div>
        </div>

        <LoanFilters
          filterStatus={filterStatus}
          onFilterChange={setFilterStatus}
          search={search}
          onSearchChange={setSearch}
          filterLoai={filterLoai}
          onFilterLoaiChange={setFilterLoai}
          statusCounts={statusCounts}
          typeCounts={typeCounts}
        />

        {filteredLoans.length === 0 ? (
          <EmptyState
            icon="ri-money-cny-circle-line"
            title="Chưa có yêu cầu vay"
            description="Tạo yêu cầu vay vốn hoặc vật tư để HTX xét duyệt."
          />
        ) : (
          <div className="space-y-2 mt-3">
            {filteredLoans.map((loan) => (
              <LoanCard
                key={loan.id}
                loan={loan}
                season={seasons.find((s) => s.id === loan.seasonId)}
                onView={() => setDetailItem(loan)}
                onDelete={loan.status === 'Chờ kiểm tra' ? () => handleDelete(loan) : undefined}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create form modal — unified 3-step wizard (also used from the overview quick actions) */}
      <ApplyLoanSheet
        open={formOpen}
        onClose={() => setFormOpen(false)}
        farmerId={farmerId}
        kyc={kyc}
      />

      {/* Detail modal */}
      {detailItem && (
        <LoanDetailModal
          loan={detailItem}
          season={seasons.find((s) => s.id === detailItem.seasonId)}
          debt={debts.find((debt) => debt.advanceRequestId === detailItem.id)}
          hasPendingPayment={(() => {
            const debt = debts.find((d) => d.advanceRequestId === detailItem.id);
            if (!debt) return false;
            return getDebtDisplayStatus(debt).label !== debt.status;
          })()}
          onPayDebt={() => setRepayOpen(true)}
          onClose={() => setDetailItem(null)}
        />
      )}

      {/* Repayment sheet — real PaymentIntent flow (QR + server-issued transfer memo) */}
      <RepayDebtSheet open={repayOpen} onClose={() => setRepayOpen(false)} farmerId={farmerId} />
    </div>
  );
}
